/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { ScreenController } from '../../core/navigation/ScreenController';
import type { RootState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { TGalleryScreenState } from '../../core/types';
import { GalleryScreenComponent } from '../../core/usecases';
import { GALLERY_UPDATE_GHOST_MODE } from '../galleryscreen/galleryscreen.types';
import { showErrorOverlayGalleryScreen } from '../overlay/galleryscreennonetworkoverlay/galleryscreennonetworkoverlay.reducers';
import { setGridFocusAction, setGridItemFocusAction, setGridItemsAction } from './grid.actions';
import {
    REMOVE_GRID_ITEMS,
    SET_GRID_COLUMN_LIMIT,
    SET_GRID_FOCUS,
    SET_GRID_ITEM_FOCUS,
    SET_GRID_ITEMS,
    SET_GRID_ROW_LIMIT,
} from './grid.types';
import { IGridEntry, IGridState } from './gridprops';

const initialState: IGridState = {};

const gridReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<{ id: string; entry: IGridEntry } & IGridEntry>>{},
) => {
    switch (type) {
        case GALLERY_UPDATE_GHOST_MODE:
            return {
                ...state,
                [payload.id]: {
                    ...state[payload.id],
                    ghostMode: payload.ghostMode,
                },
            };
        case SET_GRID_ITEMS:
            return { ...state, [payload.id]: payload.entry };
        case REMOVE_GRID_ITEMS: {
            const { [payload.id]: _, ...newState } = state;
            return newState;
        }
        case SET_GRID_COLUMN_LIMIT:
            return {
                ...state,
                [payload.id]: { ...state[payload.id], columnLimit: payload.columnLimit },
            };
        case SET_GRID_ROW_LIMIT:
            return {
                ...state,
                [payload.id]: { ...state[payload.id], rowLimit: payload.rowLimit },
            };
        case SET_GRID_ITEM_FOCUS:
            return {
                ...state,
                [payload.id]: { ...state[payload.id], focusedItem: payload.focusedItem },
            };
        case SET_GRID_FOCUS:
            return {
                ...state,
                [payload.id]: { ...state[payload.id], isActive: payload.isActive },
            };

        default:
            return state;
    }
};

const setGridForNextPage = async (dispatch, id: string, grid: IGridEntry) => {
    const screenProperties = ScreenController.getInstance()
        .getScreen<TGalleryScreenState>()
        .getProperties();
    const parentVodItemId = screenProperties?.parentVodItemId;
    const currentPage = GalleryScreenComponent.getCurrentPage();
    if (!parentVodItemId) {
        Diagnostics.error('GalleryGridReducer', 'parentVodItemId is undefined');
    }

    if (!grid) return;
    dispatch(
        setGridItemsAction(id, {
            ...grid,
            loadingState: true,
        }),
    );

    const { focusedItem } = grid;
    const galleryScreenGridGenerator = new GalleryScreenComponent();
    if (!parentVodItemId) {
        Diagnostics.error('GalleryGridReducer', 'parentVodItemId is undefined');
        return;
    }
    const resultForNextPage = await galleryScreenGridGenerator.getNextGalleryPage(
        parentVodItemId,
        focusedItem,
    );

    if (currentPage + 1 !== GalleryScreenComponent.getCurrentPage()) return;

    if (resultForNextPage?.response?.errorCode) {
        dispatch(showErrorOverlayGalleryScreen(resultForNextPage.response.errorCode));
        return;
    }

    const gridParamsForNextPage = resultForNextPage?.gridParams;
    if (!gridParamsForNextPage) {
        Diagnostics.error('GalleryGridReducer', 'gridParamsForNextPage is undefined');
        return;
    }
    const { colLimit, lastcolumn } = gridParamsForNextPage;
    const newItemsForNextPage = resultForNextPage?.response?.items;

    if (newItemsForNextPage) {
        dispatch(
            setGridItemsAction(id, {
                isActive: true,
                items: newItemsForNextPage,
                focusedItem: { row: 0, column: lastcolumn },
                rowLimit: grid.rowLimit,
                columnLimit: colLimit,
                ghostMode: false,
                loadingState: false,
            }),
        );
    }
};

export const onKeyDownGrid = (id: string) => async (dispatch, getState: () => RootState) => {
    const state = getState();
    const grid = state.grid[id];
    if (!grid) return;
    const { focusedItem, ghostMode, rowLimit, columnLimit, items } = grid;
    const totalItems = Array.isArray(items) ? items.length : 0;

    const newRow = Math.min(focusedItem.row + 1, rowLimit - 1);
    const { column } = focusedItem;
    const targetIndex = newRow * columnLimit + column;
    const lastcolumn =
        column > (totalItems - 1) % columnLimit ? (totalItems - 1) % columnLimit : column;

    const currentPage = GalleryScreenComponent.getCurrentPage();
    const totalItemsInGallery = GalleryScreenComponent.getTotalItems();
    const totalAvailableItems = GalleryScreenComponent.getItemsSeenSoFar(currentPage);

    if (ghostMode) return;

    if (focusedItem.row === rowLimit - 1 && totalAvailableItems < totalItemsInGallery) {
        await setGridForNextPage(dispatch, id, grid);
        return;
    }
    if (targetIndex >= totalItems) {
        const lastRow = Math.floor((totalItems - 1) / columnLimit);
        dispatch(
            setGridItemFocusAction(id, {
                row: lastRow,
                column: lastcolumn,
            }),
        );
    } else {
        dispatch(setGridItemFocusAction(id, { row: newRow, column }));
    }
};

const setGridForPrevPage = async (dispatch, id: string, grid: IGridEntry) => {
    const screenProperties = ScreenController.getInstance()
        .getScreen<TGalleryScreenState>()
        .getProperties();
    const parentVodItemId = screenProperties?.parentVodItemId;
    const currentPage = GalleryScreenComponent.getCurrentPage();

    if (!parentVodItemId) {
        Diagnostics.error('GalleryGridReducer', 'parentVodItemId is undefined');
        return;
    }

    if (!grid) return;
    dispatch(
        setGridItemsAction(id, {
            ...grid,
            loadingState: true,
        }),
    );

    const galleryScreenGridGenerator = new GalleryScreenComponent();
    const { focusedItem } = grid;
    const res = await galleryScreenGridGenerator.getPrevGalleryPage(parentVodItemId, focusedItem);

    if (currentPage - 1 !== GalleryScreenComponent.getCurrentPage()) return;

    if (res?.response?.errorCode) {
        dispatch(showErrorOverlayGalleryScreen(res.response.errorCode));
        return;
    }
    const gridParams = res?.gridParams;
    if (!gridParams) {
        Diagnostics.error('GalleryGridReducer', 'gridParams is undefined');
        return;
    }
    const { rowLimit, colLimit, lastcolumn } = res?.gridParams;

    const newItems = res?.response?.items;

    if (newItems) {
        dispatch(
            setGridItemsAction(id, {
                isActive: true,
                items: newItems,
                focusedItem: { row: rowLimit - 1, column: lastcolumn },
                rowLimit,
                columnLimit: colLimit,
                ghostMode: false,
                loadingState: false,
            }),
        );
    }
};

export const onKeyUpGrid = (id: string) => async (dispatch, getState: () => RootState) => {
    const state = getState();
    const grid = state.grid[id];
    if (!grid) return;

    const { focusedItem, ghostMode } = grid;
    const newRow = Math.max(focusedItem.row - 1, 0);
    const currentPage = GalleryScreenComponent.getCurrentPage();

    if (ghostMode) return;

    if (focusedItem.row === 0 && currentPage !== 0) {
        await setGridForPrevPage(dispatch, id, grid);
    } else {
        dispatch(setGridItemFocusAction(id, { row: newRow, column: focusedItem.column }));
    }
};

export const onKeyLeftGrid = (id: string) => (dispatch, getState: () => RootState) => {
    const state = getState();
    const grid = state.grid[id];
    if (!grid) return;

    const { focusedItem, ghostMode, columnLimit, items } = grid;
    const totalItems = Array.isArray(items) ? items.length : 0;
    const currentRow = focusedItem.row;
    const currentCol = focusedItem.column;

    if (ghostMode) return;

    // If focused item is leftmost and a row above exists, move to top row's rightmost column
    if (currentCol === 0 && currentRow > 0) {
        const prevRow = currentRow - 1;
        const lastColInPrevRow =
            prevRow === Math.floor((totalItems - 1) / columnLimit)
                ? (totalItems - 1) % columnLimit
                : columnLimit - 1;
        dispatch(setGridItemFocusAction(id, { row: prevRow, column: lastColInPrevRow }));
        return;
    }

    // Otherwise, move left within the row
    const newCol = Math.max(currentCol - 1, 0);
    dispatch(setGridItemFocusAction(id, { row: currentRow, column: newCol }));
};

export const onKeyRightGrid = (id: string) => (dispatch, getState: () => RootState) => {
    const state = getState();
    const grid = state.grid[id];
    if (!grid) return;

    const { focusedItem, ghostMode, columnLimit, rowLimit, items } = grid;
    const totalItems = Array.isArray(items) ? items.length : 0;
    const currentCol = focusedItem.column;
    const currentRow = focusedItem.row;

    if (ghostMode) return;
    // Calculate last column in current row
    const lastColInRow =
        currentRow === Math.floor((totalItems - 1) / columnLimit)
            ? (totalItems - 1) % columnLimit
            : columnLimit - 1;
    const isRightmost = currentCol === lastColInRow;

    // If focused item is rightmost element of the row, move to next row's first item if available
    if (isRightmost) {
        const nextRow = currentRow + 1;
        const nextIndex = nextRow * columnLimit;
        if (nextRow < rowLimit && nextIndex < totalItems) {
            dispatch(setGridItemFocusAction(id, { row: nextRow, column: 0 }));
            return;
        }
    }
    // Otherwise, move right within the row
    const newCol = Math.min(currentCol + 1, lastColInRow);
    dispatch(setGridItemFocusAction(id, { row: currentRow, column: newCol }));
};

export const onMouseOverGrid = () => (dispatch: Function, getState: Function) => {
    // TODO: Implement the logic for handling mouse over event in grid
};

export const onMouseOutGrid = () => (dispatch: Function, getState: Function) => {
    // TODO: Implement the logic for handling mouse out event in grid
};

export const resetGrid = (id: string) => (dispatch: Function) => {
    dispatch(
        setGridItemsAction(id, {
            items: [],
            focusedItem: { row: 0, column: 0 },
            rowLimit: 0,
            columnLimit: 0,
            ghostMode: false,
            isActive: false,
        }),
    );
    dispatch(setGridItemFocusAction(id, { row: 0, column: 0 }));
};

export const setGridFocus = (id: string, isFocused: boolean) => (dispatch: Function) => {
    dispatch(setGridFocusAction(id, isFocused));
};

export default gridReducer;
